import { useState } from "react";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ENQUIRY_TYPES, FORMSPREE_FORM_ID } from "@/config/site";

export const ContactForm = () => {
  const [status, setStatus] = useState("idle");
  const [enquiryType, setEnquiryType] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting") return;

    const form = e.currentTarget;
    const formData = new FormData(form);

    if (!FORMSPREE_FORM_ID) {
      setStatus("error");
      setErrorMsg("The contact form isn't connected yet — add REACT_APP_FORMSPREE_FORM_ID in frontend/.env.");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");
    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_FORM_ID}`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
      form.reset();
      setEnquiryType("");
      if (typeof window !== "undefined" && window.gtag) window.gtag("event", "contact_form_submitted");
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong sending your message — please try again or email us directly.");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="contact-success-state" className="flex flex-col items-center gap-3 border border-brand-purple bg-brand-purple/10 px-6 py-16 text-center">
        <CheckCircle2 className="h-10 w-10 text-brand-purple" />
        <p className="font-heading text-3xl uppercase text-white">Message Sent</p>
        <p className="text-sm text-white/60">Thanks for getting in touch — we&apos;ll come back to you soon.</p>
        <Button data-testid="contact-send-another-btn" variant="outline" className="mt-2 rounded-none border-white/20 text-white hover:bg-white/10" onClick={() => setStatus("idle")}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="grid gap-6" onFocus={() => window.gtag && window.gtag("event", "contact_form_started")}>
      <input type="text" name="_gotcha" className="hidden" tabIndex="-1" autoComplete="off" aria-hidden="true" />

      <div className="grid gap-2">
        <Label htmlFor="name" className="text-white/70">Name</Label>
        <Input data-testid="contact-input-name" id="name" name="name" required className="rounded-none border-border-dark bg-surface-dark text-white" />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="email" className="text-white/70">Email</Label>
        <Input data-testid="contact-input-email" id="email" name="email" type="email" required className="rounded-none border-border-dark bg-surface-dark text-white" />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="organisation" className="text-white/70">Organisation / Club</Label>
        <Input data-testid="contact-input-organisation" id="organisation" name="organisation" className="rounded-none border-border-dark bg-surface-dark text-white" />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="enquiryType" className="text-white/70">Enquiry Type</Label>
        <Select value={enquiryType} onValueChange={setEnquiryType}>
          <SelectTrigger data-testid="contact-select-enquiry-type" id="enquiryType" className="rounded-none border-border-dark bg-surface-dark text-white">
            <SelectValue placeholder="Select an enquiry type" />
          </SelectTrigger>
          <SelectContent>
            {ENQUIRY_TYPES.map((type) => (
              <SelectItem key={type} value={type} data-testid={`contact-enquiry-option-${type.toLowerCase().replace(/\s+/g, "-")}`}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" name="enquiryType" value={enquiryType} />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="message" className="text-white/70">Message</Label>
        <Textarea data-testid="contact-input-message" id="message" name="message" rows={6} required className="rounded-none border-border-dark bg-surface-dark text-white" />
      </div>

      {status === "error" && (
        <div data-testid="contact-error-state" role="alert" className="flex items-start gap-2 border border-live-red/50 bg-live-red/10 px-4 py-3 text-sm text-live-red">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> {errorMsg}
        </div>
      )}

      <Button
        type="submit"
        data-testid="contact-submit-btn"
        disabled={status === "submitting"}
        className="h-12 rounded-none bg-brand-purple font-subheading uppercase tracking-wide text-white hover:bg-brand-purple-hover"
      >
        {status === "submitting" ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…
          </>
        ) : (
          "Send Enquiry"
        )}
      </Button>
    </form>
  );
};
